import { Component, computed, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '../../core/auth.service';
import { LayoutService } from '../../core/layout.service';
import { IconComponent, IconName } from '../icon/icon.component';

interface NavItem {
  path: string;
  labelKey: string;
  icon: IconName;
  /** Omit for items every authenticated user can see; set to gate by role (any match shows the item). */
  roles?: string[];
}

const ALL_NAV_ITEMS: NavItem[] = [
  { path: '/dashboard', labelKey: 'shared.nav.homeDashboard', icon: 'dashboard' },
  { path: '/work-orders', labelKey: 'shared.nav.workOrders', icon: 'clipboard-list' },
  { path: '/maintenance-requests', labelKey: 'shared.nav.maintenanceRequests', icon: 'wrench' },
  { path: '/inventory', labelKey: 'shared.nav.inventory', icon: 'boxes', roles: ['StoreInventoryManager'] },
  { path: '/stores', labelKey: 'shared.nav.stores', icon: 'warehouse', roles: ['StoreInventoryManager', 'FacilityManager'] },
  { path: '/parts', labelKey: 'shared.nav.parts', icon: 'package', roles: ['StoreInventoryManager'] },
  {
    path: '/purchase-orders',
    labelKey: 'shared.nav.purchaseOrders',
    icon: 'cart',
    roles: ['StoreInventoryManager', 'FacilityManager']
  },
  {
    path: '/vendors',
    labelKey: 'shared.nav.vendors',
    icon: 'truck',
    roles: ['StoreInventoryManager', 'FacilityManager', 'SystemAdmin']
  },
  { path: '/assets', labelKey: 'shared.nav.assets', icon: 'server', roles: ['AssetManager'] },
  { path: '/admin/buildings', labelKey: 'shared.nav.buildings', icon: 'building', roles: ['SystemAdmin', 'FacilityManager'] },
  {
    path: '/admin/priority-matrix',
    labelKey: 'shared.nav.priorityMatrix',
    icon: 'sliders',
    roles: ['SystemAdmin', 'FacilityManager']
  },
  {
    path: '/admin/maintenance-categories',
    labelKey: 'shared.nav.maintenanceCategories',
    icon: 'tag',
    roles: ['SystemAdmin', 'FacilityManager']
  },
  { path: '/admin/users', labelKey: 'shared.nav.adminUsers', icon: 'users', roles: ['SystemAdmin'] },
  { path: '/admin/audit-log', labelKey: 'shared.nav.auditLog', icon: 'history', roles: ['SystemAdmin'] },
  { path: '/admin/imports', labelKey: 'shared.nav.imports', icon: 'upload', roles: ['SystemAdmin'] },
  {
    path: '/admin/notification-templates',
    labelKey: 'shared.nav.notificationTemplates',
    icon: 'mail',
    roles: ['SuperAdmin']
  },
  { path: '/admin/notification-log', labelKey: 'shared.nav.notificationLog', icon: 'bell', roles: ['SystemAdmin'] },
  { path: '/settings', labelKey: 'shared.nav.settings', icon: 'settings' },
  { path: '/admin/agents', labelKey: 'shared.nav.agents', icon: 'bot', roles: ['SystemAdmin', 'FacilityManager'] },
  { path: '/admin/organizations', labelKey: 'shared.nav.organizations', icon: 'network', roles: ['SuperAdmin'] }
];

/** Left-hand nav shell. ALL_NAV_ITEMS is the single place to add the next destination. */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslatePipe, IconComponent],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent {
  private readonly auth = inject(AuthService);
  readonly layout = inject(LayoutService);

  /**
   * SuperAdmin has no operational access to any Organization's data by default
   * (docs/design/06_multi_tenant_organizations.md §1), so it gets its own filter branch: only items
   * explicitly gated to SuperAdmin show, rather than also picking up every role-unrestricted item
   * (Dashboard, Settings) the way every other role does.
   */
  readonly navItems = computed(() =>
    this.auth.hasRole('SuperAdmin')
      ? ALL_NAV_ITEMS.filter((item) => item.roles?.includes('SuperAdmin'))
      : ALL_NAV_ITEMS.filter((item) => !item.roles || item.roles.some((role) => this.auth.hasRole(role)))
  );
}
